import { Box, Button, IconButton, Stack, styled, Tooltip, Typography } from '@mui/material'
import { useIsMutating, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiResponse, CustomerService, ICustomer } from '@/api/services/CustomerService'
import { setCurrentUser } from '@/db/indexedDB'
import { useAppStore } from '@/store'

import {
  capitalizeWords,
  formatDate,
  replaceCharacterWithSpace,
  transformedColumns,
} from '@/utils/common'

import {
  EMAIL_VERIFICATION_KEY,
  PHONE_VERIFICATION_KEY,
  VERIFICATION_STATUS,
} from '@/utils/constants'

import SearchLogo from '@/assets/search.svg'
import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { NotVerifiedIcon, VerifiedIcon } from '@/components/UI/Icons'
import { Search } from '@/components/UI/Search'
import { PlatformPermission } from '@/domain/permissions/rbac'
import { usePermissions } from '@/domain/permissions/usePermissions'
import { ErrorPage } from '@/pages/Error'
import { useAuth0 } from '@auth0/auth0-react'
import { DataTable } from '../UI/DataTable'
import { Paginator } from '../UI/Paginator'
import SkeletonRow from '../UI/Skeleton'
import { DeleteCustomer } from './DeleteCustomer'

const VerifiedContent = ({
  user,
  value,
  fieldKey,
}: {
  user: ICustomer
  value: string
  fieldKey: keyof ICustomer
}) => {
  const status = user[fieldKey].toLowerCase()
  const isNotVerified =
    typeof status === 'string' &&
    (status === VERIFICATION_STATUS.FAILED ||
      status === VERIFICATION_STATUS.NOT_VERIFIED ||
      status === VERIFICATION_STATUS.NOT_VERIFIED_ENROLLED_MFA)

  return (
    <>
      <StyledText>{value}</StyledText>
      {typeof status === 'string' && (
        <Tooltip title={capitalizeWords(replaceCharacterWithSpace(status, '_'))}>
          <IconButton>{isNotVerified ? <NotVerifiedIcon /> : <VerifiedIcon />}</IconButton>
        </Tooltip>
      )}
    </>
  )
}

const columns: ITableColumn<ICustomer>[] = [
  {
    key: 'id',
    id: 'id',
    minWidth: '350px',
    label: 'Id',
    render: (value = '') => <StyledText>{value}</StyledText>,
  },
  {
    key: 'firstName',
    id: 'firstName',
    minWidth: '180px',
    label: 'First Name',
    render: (value = '') => <StyledText>{value}</StyledText>,
  },
  {
    key: 'lastName',
    id: 'lastName',
    minWidth: '180px',
    label: 'Last Name',
    render: (value = '') => <StyledText>{value}</StyledText>,
  },
  {
    key: 'email',
    id: 'email',
    minWidth: '480px',
    label: 'Email',
    render: (value = '', user) => (
      <Stack direction="row" spacing={1} alignItems="center">
        <VerifiedContent user={user} value={value} fieldKey={EMAIL_VERIFICATION_KEY} />
      </Stack>
    ),
  },
  {
    key: 'mobile',
    id: 'mobile',
    minWidth: '200px',
    label: 'Mobile',
    render: (value = '', user) => (
      <Stack direction="row" spacing={1} alignItems="center">
        <VerifiedContent user={user} value={value} fieldKey={PHONE_VERIFICATION_KEY} />
      </Stack>
    ),
  },
  {
    key: 'createdAt',
    id: 'createdAt',
    minWidth: '250px',
    label: 'Created at',
    render: (value = '') => <StyledText>{formatDate(value)}</StyledText>,
  },
  {
    key: 'numberOfLoans',
    id: 'numberOfLoans',
    numeric: true,
    enableSorting: false,
    label: 'No. of loans',
    render: (value = null) => <StyledText>{value}</StyledText>,
  },
]

export const CustomerList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { search, setSearch, page, pageSize, sortBy, sortOrder, setSort, setPage, setUser } =
    useAppStore()
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  const { hasPermission } = usePermissions(user?.['https://carringtonlabs.io/appRole'])
  const canDeleteUsers = hasPermission(PlatformPermission.DeleteUser)
  const isDeleting = useIsMutating({ mutationKey: ['delete-customer'] }) > 0

  const permissionBasedColumns = useMemo(() => {
    const items = [...columns]

    if (canDeleteUsers) {
      items.push({
        key: 'action',
        id: 'action',
        numeric: false,
        enableSorting: false,
        label: 'Action',
        render: (value = '', user: ICustomer) => {
          return <DeleteCustomer user={user} value={value} />
        },
      })
    }

    return items
  }, [canDeleteUsers])

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(debouncedSearch)
    }, 1000)

    return () => clearTimeout(handler)
  }, [debouncedSearch])

  const handleSearch = (term: string) => {
    setDebouncedSearch(term)
  }

  const handleClear = () => {
    setDebouncedSearch('')
    setSearch('')
    setSort('', 'asc')
    queryClient.removeQueries({ queryKey: ['customers'] })
  }

  const {
    data: customers,
    isLoading,
    isError,
  } = useQuery<ApiResponse<ICustomer>>({
    queryKey: ['customers', search, page, pageSize, sortBy, sortOrder],
    queryFn: () => CustomerService.getAll({ search, page, pageSize, sortBy, sortOrder }),
    enabled: !!search,
    placeholderData: prev => prev,
  })

  const handleCustomerRowClick = async (user: ICustomer) => {
    setUser(user)
    await setCurrentUser(user)
    navigate('/profile')
  }
  const SearchBar = (
    <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} px={2} mb={2}>
      {debouncedSearch && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleClear}
          sx={{
            borderColor: '#E4E7EC',
            color: '#344054',
            fontWeight: 500,
            fontSize: 13,
            textTransform: 'none',
            borderRadius: '8px',
            height: 36,
            '&:hover': { borderColor: '#D0D5DD', backgroundColor: '#F9FAFB' },
          }}
        >
          Clear
        </Button>
      )}
      <Search onSearch={handleSearch} searchValue={debouncedSearch} />
    </Box>
  )

  if (isLoading || isDeleting) {
    return (
      <>
        {SearchBar}
        <SkeletonRow columns={columns} />
      </>
    )
  }

  const hasCustomers = !!customers?.data?.length
  return (
    <>
      {SearchBar}

      {isError ? (
        <ErrorPage
          title="Unable to Fetch Customers"
          message="We couldn't retrieve customer data at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        />
      ) : hasCustomers ? (
        <DataTable<ICustomer>
          rows={customers.data}
          columns={
            transformedColumns(permissionBasedColumns, 'Customers') as ITableColumn<ICustomer>[]
          }
          sortBy={sortBy as keyof ICustomer}
          sortOrder={sortOrder}
          onSort={(column, order) => setSort(column as string, order)}
          handleRowClick={handleCustomerRowClick}
        />
      ) : (
        <EmptyContainer>
          <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
          {search && customers?.data?.length === 0 ? (
            <>
              <Title>No Customers Found</Title>
              <Message>
                Please try adjusting your filters or search criteria to find what you're looking
                for.
              </Message>
            </>
          ) : (
            <Message>Start by entering search criteria to find a client.</Message>
          )}
        </EmptyContainer>
      )}

      {!isError && (customers?.pagination?.totalRecords ?? 0) > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          mt={2}
          py={1}
          sx={{
            border: '1px solid #E4E7EC',
            borderRadius: '8px',
          }}
        >
          <Paginator
            totalPages={Number(customers?.pagination?.totalPages ?? 1)}
            currentPage={page}
            onChange={setPage}
          />
        </Box>
      )}
    </>
  )
}

const StyledText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  textAlign: 'left',
  color: theme.palette.secondary.main,
}))

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: '1px solid #E4E7EC',
  borderRadius: '8px',
  padding: '3rem 0',
})

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
  textAlign: 'center',
}))
